import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChildren, ViewEncapsulation } from "@angular/core";
import { Meeting } from "@common/ADAPT.Common.Model/organisation/meeting";
import { MeetingAgendaItem, MeetingAgendaItemType } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-item";
import { MeetingAgendaTemplate } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-template";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { Logger } from "@common/lib/logger/logger";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { IConfirmationDialogData } from "@common/ux/adapt-common-dialog/confirmation-dialog.component/confirmation-dialog.component";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { ChangeManagerService } from "@common/ux/change-manager/change-manager.service";
import { IDxListItemReorderedEvent } from "@common/ux/dx.types";
import { InitializedEvent } from "devextreme/ui/text_box";
import { BehaviorSubject, EMPTY, forkJoin, lastValueFrom, Observable, of, Subscription, timer } from "rxjs";
import { catchError, concatMap, debounceTime, delay, finalize, map, switchMap, tap } from "rxjs/operators";
import { EditAgendaItemComponent } from "../edit-agenda-item/edit-agenda-item.component";
import { MeetingsService } from "../meetings.service";
import { MeetingsUiService } from "../meetings-ui.service";

interface IMeetingAgendaItems {
    agendaItems: MeetingAgendaItem[];
    preWork?: MeetingAgendaItem;
    facilitationNotes?: MeetingAgendaItem;
}

@Component({
    selector: "adapt-edit-agenda",
    templateUrl: "./edit-agenda.component.html",
    styleUrls: ["../meeting-styles.scss", "./edit-agenda.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class EditAgendaComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
    @Input() public meeting?: Meeting;

    @Input() public agendaTemplate?: MeetingAgendaTemplate;
    @Output() public agendaItemsChange = new EventEmitter<MeetingAgendaItem[]>();

    @Input() public saveOnChange = true;
    @Input() public hideNotesAndItems = false;
    @Input() public disabled = false;

    @Input() public expandItemsOnInit = false;
    @Output() public expandChange = new EventEmitter<boolean>();
    @Output() public canExpandChange = new EventEmitter<boolean>();
    @Output() public isAddingNewAgendaItem = new EventEmitter<boolean>();
    @ViewChildren(EditAgendaItemComponent) private agendaItemComponents!: QueryList<EditAgendaItemComponent>;

    public canExpand = false;

    public agendaItems$?: Observable<IMeetingAgendaItems>;
    public newAgendaItem?: MeetingAgendaItem;
    public isImporting = false;
    public savingImport = false;

    protected readonly log = Logger.getLogger(this.constructor.name);

    private preWork?: MeetingAgendaItem;
    private facilitationNotes?: MeetingAgendaItem;
    private agendaItems: MeetingAgendaItem[] = [];
    private removedItems: MeetingAgendaItem[] = [];
    private triggerUpdate$ = new BehaviorSubject<void>(undefined);

    private unregisterCleanupFunction: (() => void);
    private importSubscription?: Subscription;

    public get isReadonly() {
        const isSystem = !!this.agendaTemplate?.code;
        return isSystem || this.disabled;
    }

    public get totalPlannedDurationInMinutes() {
        return MeetingsService.getMeetingAgendaItemsTotalPlannedDurationInMinutes(this.agendaItems);
    }

    public get hasPreWork() {
        return !!this.preWork;
    }

    public get hasFacilitationNotes() {
        return !!this.facilitationNotes;
    }

    constructor(
        public meetingsService: MeetingsService,
        private commonDataService: CommonDataService,
        private meetingsUiService: MeetingsUiService,
        private dialogService: AdaptCommonDialogService,
        rxjsBreezeService: RxjsBreezeService,
        changeManager: ChangeManagerService,
    ) {
        super();
        rxjsBreezeService.entityTypeChanged(MeetingAgendaItem).pipe(
            this.takeUntilDestroyed(),
        ).subscribe((item) => {
            if ((item.meetingId && item.meetingId === this.meeting?.meetingId) ||
                (item.meetingAgendaTemplateId && item.meetingAgendaTemplateId === this.agendaTemplate?.meetingAgendaTemplateId)) {
                this.triggerUpdate$.next();
            }
        });

        rxjsBreezeService.entityTypeDetached(MeetingAgendaItem).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => {
            // detached items should be removed from removedItems collection
            this.removedItems = this.removedItems.filter((i) => !i.entityAspect.entityState.isDetached());
        });

        this.unregisterCleanupFunction = changeManager.registerCleanupFunction(() => this.cleanupNewAgendaItem());
    }

    public async ngOnDestroy() {
        await this.cleanupNewAgendaItem(true);
        await this.cleanupImporting();
        this.unregisterCleanupFunction();
        super.ngOnDestroy();
    }

    public ngOnInit() {
        if (this.meeting || this.agendaTemplate) {
            this.agendaItems$ = this.triggerUpdate$.pipe(
                debounceTime(10),
                switchMap(() => this.cleanupNewAgendaItem()),
                switchMap(() => this.cleanupImporting()),
                concatMap(() => this.getAgendaItems()),
            );
        }
    }

    public ngOnChanges(changes: SimpleChanges) {
        if ((changes.meeting && this.meeting) || (changes.agendaTemplate && this.agendaTemplate)) {
            this.triggerUpdate$.next();
        }
    }

    public emitAgendaItemsChange() {
        this.agendaItemsChange.emit(this.allItems);
    }

    private get allItems() {
        return [
            ...(this.preWork ? [this.preWork] : []),
            ...(this.facilitationNotes ? [this.facilitationNotes] : []),
            ...this.agendaItems,
            ...this.removedItems,
        ];
    }

    public async onItemDeleted(item: MeetingAgendaItem) {
        if (!this.saveOnChange) {
            if (item.supplementaryData) {
                // if is a new entity, need to reject changes
                item.supplementaryData.entityAspect.rejectChanges();
            }

            const removedItem = await lastValueFrom(this.commonDataService.remove(item));
            if (removedItem) {
                this.removedItems.push(removedItem as MeetingAgendaItem);

                // update the ordinals of all the items after the removed item
                const itemIndex = this.agendaItems.indexOf(removedItem);
                ArrayUtilities.removeElementFromArray(removedItem, this.agendaItems);
                SortUtilities.updateIntegerSortedArrayAfterItemRemoval(this.agendaItems, "ordinal", itemIndex);
            }
        } // else deletion is already saved - simply update

        this.triggerUpdate$.next();
    }

    @Autobind
    public createNewPreWorkAgendaItem() {
        return this.createNewAgendaItem(true);
    }

    @Autobind
    public createNewFacilitationNotesAgendaItem() {
        return this.createNewAgendaItem(false, true);
    }

    @Autobind
    public createNewAgendaItem(isPreWork = false, isFacilitationNotes = false) {
        if (this.meeting || this.agendaTemplate) {
            const maxOrdinal = this.maxItemOrdinal;
            const type = isPreWork
                ? MeetingAgendaItemType.PreWork
                : isFacilitationNotes
                    ? MeetingAgendaItemType.FacilitationNotes
                    : MeetingAgendaItemType.AgendaItem;
            return this.meetingsService.createAgendaItem(
                maxOrdinal + 1,
                this.meeting?.meetingId,
                this.agendaTemplate?.meetingAgendaTemplateId,
                type,
            ).pipe(
                switchMap((agendaItem) => {
                    if (isPreWork && isFacilitationNotes) {
                        throw new Error("cannot create pre-work and facilitation notes at the same time");
                    }

                    if (isPreWork) {
                        agendaItem.name = "Pre-work";
                        agendaItem.ordinal = -1;
                        // don't assign this.preWork here so that it will be emitted to refresh the display
                    }

                    if (isFacilitationNotes) {
                        agendaItem.name = "Facilitation notes";
                        // facilitation notes should show above pre-work
                        agendaItem.ordinal = -2;
                    }

                    if (isPreWork || isFacilitationNotes) {
                        return this.addNewAgendaItem(agendaItem);
                    } else {
                        this.newAgendaItem = agendaItem;
                        this.isAddingNewAgendaItem.emit(true);
                        this.emitAgendaItemsChange();
                    }

                    return EMPTY;
                }),
            );
        } else {
            return EMPTY;
        }
    }

    @Autobind
    public onImportClicked() {
        this.isImporting = true;
        this.savingImport = false;
        const importDialog = this.meetingsUiService.importMeetingAgenda({ saveOnClose: this.saveOnChange, currentMeeting: this.meeting, currentTeam: this.agendaTemplate?.team });

        this.importSubscription = importDialog.pipe(
            delay(0), // next digest cycle to avoid ExpressionCHangedAfterItHasBeenCheckedError
            tap(() => this.savingImport = true),
            switchMap((items) => this.importFromAgendaItems(items)),
            catchError(() => this.cleanupImporting()),
            finalize(() => {
                this.isImporting = false;
                this.savingImport = false;
                this.importSubscription = undefined;
            }),
        ).subscribe();
    }

    @Autobind
    public addNewAgendaItem(newAgendaItem?: MeetingAgendaItem) {
        const savedAgendaItem = newAgendaItem ?? this.newAgendaItem;
        if (savedAgendaItem && this.saveOnChange) {
            return this.meetingsService.saveEntities([savedAgendaItem]).pipe(
                tap(() => {
                    this.newAgendaItem = undefined;
                    this.triggerUpdate$.next();
                    this.scrollAgendaItemIntoView(savedAgendaItem);
                }),
            );
        } else {
            // do this in the next digest cycle or there will be error with unsubscribe from the button destroy
            return timer(0).pipe(
                tap(() => {
                    if (savedAgendaItem?.type === MeetingAgendaItemType.PreWork) {
                        this.preWork = savedAgendaItem;
                    } else if (savedAgendaItem?.type === MeetingAgendaItemType.FacilitationNotes) {
                        this.facilitationNotes = savedAgendaItem;
                    }

                    this.newAgendaItem = undefined;
                    this.isAddingNewAgendaItem.emit(false);
                    this.triggerUpdate$.next();
                    this.scrollAgendaItemIntoView(savedAgendaItem);
                }),
            );
        }
    }

    private scrollAgendaItemIntoView(agendaItem?: MeetingAgendaItem) {
        setTimeout(() => {
            const addedItemComponent = this.agendaItemComponents.find((cmp) => cmp.item === agendaItem);
            addedItemComponent?.scrollIntoView();
        }, 250);
    }

    @Autobind
    public async cancelNewAgendaItem() {
        if (this.newAgendaItem) {
            await lastValueFrom(this.commonDataService.remove(this.newAgendaItem));
            this.newAgendaItem = undefined;
            this.isAddingNewAgendaItem.emit(false);
            this.emitAgendaItemsChange();
        }
    }

    public canExpandChanged(canExpand: boolean) {
        if (this.agendaItemComponents) {
            this.canExpand = this.agendaItemComponents.some((i) => i.canExpand);
        } else {
            this.canExpand = canExpand;
        }

        this.canExpandChange.emit(this.canExpand);
    }

    public expandChanged(expanded: boolean) {
        if (expanded) {
            this.expandChange.emit(expanded);
        } else {
            this.expandChange.emit(this.canExpand && this.agendaItemComponents.some((i) => i.expandDetails));
        }
    }

    public setExpandAll(expanded: boolean) {
        this.agendaItemComponents.forEach((i) => i.expandDetails = expanded);
    }

    public onInitialized(e: InitializedEvent) {
        setTimeout(() => e.component?.focus());
    }

    public updateOrdinals(e: IDxListItemReorderedEvent<MeetingAgendaItem>) {
        SortUtilities.reorderItemInIntegerSortedArray(this.agendaItems, "ordinal", e.fromIndex, e.toIndex);
        this.emitAgendaItemsChange();

        if (this.saveOnChange) {
            this.meetingsService.saveEntities(this.allItems).pipe(
                this.takeUntilDestroyed(),
            ).subscribe();
        }
    }

    private async cleanupNewAgendaItem(isDestroying = false) {
        if (this.newAgendaItem) {
            if (!this.newAgendaItem.name) {
                return this.cancelNewAgendaItem();
            } else if (!isDestroying && this.newAgendaItem.type === MeetingAgendaItemType.PreWork) {
                // not destroying and the new agenda item is pre work -> assign to preWork and let update handles it
                this.preWork = this.newAgendaItem;
                this.newAgendaItem = undefined;
                // newAgendaItem going to preWork -> no longer in adding new item
                this.isAddingNewAgendaItem.emit(false);
            } else if (!isDestroying && this.newAgendaItem.type === MeetingAgendaItemType.FacilitationNotes) {
                // not destroying and the new agenda item is facilitation note -> assign to preWork and let update handles it
                this.facilitationNotes = this.newAgendaItem;
                this.newAgendaItem = undefined;
                // newAgendaItem going to preWork -> no longer in adding new item
                this.isAddingNewAgendaItem.emit(false);
            } else {
                const dialogData: IConfirmationDialogData = {
                    cancelButtonText: "Discard",
                    confirmButtonText: "Save Changes",
                    title: "Unsaved Agenda Item...",
                    message: `<p>A new agenda item has been created but not yet saved.</p>
                        <p>Do you want to save the changes before continue or discard the unsaved changes?</p>
                        <p>Note that discarded changes can no longer be recovered.</p>`,
                };
                return lastValueFrom(this.dialogService.openConfirmationDialogWithBoolean(dialogData).pipe(
                    switchMap((save) => save
                        ? this.addNewAgendaItem()
                        : this.cancelNewAgendaItem()),
                ));
            }
        }
    }

    private async cleanupImporting() {
        if (this.isImporting) {
            this.isImporting = false;

            // wait for the message to go away before calling unsubscribe on the importSubscription as this may
            // possibly be from the import chain catchError (which unsubscribe will kill the dialog)
            await lastValueFrom(this.dialogService.showMessageDialog(
                "Discarding agenda items import...",
                `<p>The importing process has been discarded. If you are in the middle of saving, the save may not be completed.</p>
                <p>This can be caused by navigating away from the meeting or the meeting has been deleted by someone else from another session
                while you are saving.</p>`));

            this.importSubscription?.unsubscribe();
            this.importSubscription = undefined;
        }
    }

    private importFromAgendaItems(agendaItems: MeetingAgendaItem[]) {
        if (agendaItems.length > 0) {
            return this.meetingsService.importAgendaItems(
                agendaItems,
                this.meeting?.meetingId,
                this.agendaTemplate?.meetingAgendaTemplateId,
                this.maxItemOrdinal,
                this.hasPreWork,
                this.hasFacilitationNotes
            ).pipe(
                tap((items) => {
                    if (!this.preWork) {
                        this.preWork = ArrayUtilities.getSingleFromArray(items.filter((i) => i.extensions.isPreWork));
                    }

                    if (!this.facilitationNotes) {
                        this.facilitationNotes = ArrayUtilities.getSingleFromArray(items.filter((i) => i.extensions.isFacilitationNotes));
                    }
                }),
                switchMap(() => this.getAgendaItems()),
                switchMap((items) => {
                    // ensure pre-work has ordinal -1, others will just be reordered
                    if (items.preWork) {
                        items.preWork.ordinal = -1;
                    }
                    if (items.facilitationNotes) {
                        items.facilitationNotes.ordinal = -2;
                    }
                    SortUtilities.sequenceNumberFieldInArray(items.agendaItems, "ordinal");

                    if (this.saveOnChange) {
                        const allItems = this.allItems; // this will include preWork and others, getAgendaItems() above already update them
                        return this.commonDataService.saveEntities([
                            ...allItems,
                            ...allItems
                                .filter((i) => !!i.supplementaryData) // removed items won't have supp entity
                                .map((i) => i.supplementaryData!),
                        ]);
                    } else {
                        return of(undefined);
                    }
                }),
                tap(() => {
                    this.triggerUpdate$.next();
                }),
                this.takeUntilDestroyed(),
            );
        } else {
            return EMPTY;
        }
    }

    private getAgendaItems() {
        const itemsObservable = this.meeting
            ? this.meetingsService.getAgendaItemsForMeeting(this.meeting)
            : this.meetingsService.getAgendaItemsForMeetingAgendaTemplate(this.agendaTemplate!.meetingAgendaTemplateId);

        return itemsObservable.pipe(
            switchMap((agendaItems) => {
                const preWorks: MeetingAgendaItem[] = [];
                const facilitationNotes: MeetingAgendaItem[] = [];
                const plainAgendaItems: MeetingAgendaItem[] = [];

                agendaItems.forEach((item) => {
                    if (item.extensions.isPreWork) {
                        preWorks.push(item);
                    } else if (item.extensions.isFacilitationNotes) {
                        facilitationNotes.push(item);
                    } else {
                        plainAgendaItems.push(item);
                    }
                });

                if (facilitationNotes.length) {
                    if (facilitationNotes.length > 1) {
                        this.log.warn(`more than 1 facilitationNotes on meeting ${this.meeting?.meetingId}. using first`);
                    }
                    this.facilitationNotes = facilitationNotes[0];
                } else {
                    this.facilitationNotes = undefined;
                }

                this.agendaItems = plainAgendaItems;

                return forkJoin([this.updateFacilitationNotes(facilitationNotes), this.updatePreWork(preWorks)]);
            }),
            map(() => {
                this.emitAgendaItemsChange();
                return {
                    agendaItems: this.agendaItems,
                    preWork: this.preWork,
                    facilitationNotes: this.facilitationNotes,
                } as IMeetingAgendaItems;
            }),
        );
    }

    private updatePreWork(preWorks: MeetingAgendaItem[]) {
        if (!this.preWork && preWorks.length > 0) {
            // no local set means this is probably a persisted meeting being restored.
            // this can happen when the persisted meeting has pre-work defined, and in another session pre-work was added and saved for the same meeting,
            // or simply when there is a pre-work in the persisted meeting.
            // we should choose the pre-work that isn't unchanged in this case to be the "my version"
            this.preWork = ArrayUtilities.getSingleFromArray(preWorks.filter((i) => !i.entityAspect.entityState.isUnchanged()));
        }

        if (preWorks.length > 1 && this.preWork?.entityAspect.entityState.isAdded()) {
            return this.dialogService.openConfirmationDialogWithBoolean({
                title: "Conflicting changes detected",
                message: "Pre-work has been defined in another session, conflicting with your changes. Please determine whether we should use their version or yours.",
                confirmButtonText: "Use their version",
                cancelButtonText: "Use my version",
            } as IConfirmationDialogData).pipe(
                switchMap((useTheirVersion) => {
                    const otherPreWorks = preWorks.filter((i) => i !== this.preWork);
                    if (useTheirVersion) {
                        // need to reject the supplementary data as well, else it can be left behind and will cause save to fail
                        this.preWork?.supplementaryData?.entityAspect.rejectChanges();
                        this.preWork?.entityAspect.rejectChanges();
                        this.preWork = ArrayUtilities.getSingleFromArray(otherPreWorks);
                        return of(undefined);
                    } else {
                        // don't need to handle supp data here, will be removed by server on save
                        return forkJoin(otherPreWorks.map((i) => this.commonDataService.remove(i))).pipe(
                            map(() => this.saveOnChange ? [...otherPreWorks, this.preWork] : otherPreWorks),
                            switchMap((savingEntities: MeetingAgendaItem[]) => this.commonDataService.saveEntities(savingEntities)),
                        );
                    }
                }),
            );
        } else {
            // excluding added preWorks as added preWork would have been moved to this.preWork on cleanup or addNewAgendaItem().
            // If this is not moved, then this component instance is not instance currently creating that preWork newAgendaItem
            // - exclude (e.g. 1 instance from dialog and 1 instance from page, dialog add entity will be fetched by the one on the page)
            if (!this.preWork || !this.preWork.entityAspect.entityState.isAdded()) {
                this.preWork = ArrayUtilities.getSingleFromArray(preWorks.filter((i) => !i.entityAspect.entityState.isAdded()));
            }

            return of(undefined);
        }
    }

    private updateFacilitationNotes(facilitationNotes: MeetingAgendaItem[]) {
        if (!this.facilitationNotes && facilitationNotes.length > 0) {
            // no local set means this is probably a persisted meeting being restored.
            // this can happen when the persisted meeting has pre-work defined, and in another session pre-work was added and saved for the same meeting,
            // or simply when there is a pre-work in the persisted meeting.
            // we should choose the pre-work that isn't unchanged in this case to be the "my version"
            this.facilitationNotes = ArrayUtilities.getSingleFromArray(facilitationNotes.filter((i) => !i.entityAspect.entityState.isUnchanged()));
        }

        if (facilitationNotes.length > 1 && this.facilitationNotes?.entityAspect.entityState.isAdded()) {
            return this.dialogService.openConfirmationDialogWithBoolean({
                title: "Conflicting changes detected",
                message: "Facilitation notes have been defined in another session, conflicting with your changes. Please determine whether we should use their version or yours.",
                confirmButtonText: "Use their version",
                cancelButtonText: "Use my version",
            } as IConfirmationDialogData).pipe(
                switchMap((useTheirVersion) => {
                    const otherPreWorks = facilitationNotes.filter((i) => i !== this.facilitationNotes);
                    if (useTheirVersion) {
                        // need to reject the supplementary data as well, else it can be left behind and will cause save to fail
                        this.facilitationNotes?.supplementaryData?.entityAspect.rejectChanges();
                        this.facilitationNotes?.entityAspect.rejectChanges();
                        this.facilitationNotes = ArrayUtilities.getSingleFromArray(otherPreWorks);
                        return of(undefined);
                    } else {
                        // don't need to handle supp data here, will be removed by server on save
                        return forkJoin(otherPreWorks.map((i) => this.commonDataService.remove(i))).pipe(
                            map(() => this.saveOnChange ? [...otherPreWorks, this.facilitationNotes] : otherPreWorks),
                            switchMap((savingEntities: MeetingAgendaItem[]) => this.commonDataService.saveEntities(savingEntities)),
                        );
                    }
                }),
            );
        } else {
            // excluding added preWorks as added preWork would have been moved to this.preWork on cleanup or addNewAgendaItem().
            // If this is not moved, then this component instance is not instance currently creating that preWork newAgendaItem
            // - exclude (e.g. 1 instance from dialog and 1 instance from page, dialog add entity will be fetched by the one on the page)
            if (!this.facilitationNotes || !this.facilitationNotes.entityAspect.entityState.isAdded()) {
                this.facilitationNotes = ArrayUtilities.getSingleFromArray(facilitationNotes.filter((i) => !i.entityAspect.entityState.isAdded()));
            }

            return of(undefined);
        }
    }

    private get maxItemOrdinal() {
        return this.agendaItems.length
            ? Math.max(...this.agendaItems.map((i) => i.ordinal))
            : -1; // max of empty array will be -infinity - set to -1 so next item will have ordinal of 0
    }
}
